import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

const IST_TIMEZONE = 'Asia/Kolkata';

export interface MarketUpdateInfo {
  /** ISO string of the latest update timestamp (UTC) */
  updatedAt: string | null;
  /** The date of the latest update in IST (YYYY-MM-DD) */
  updateDate: string | null;
  /** Whether today's update (IST) is present */
  isTodayUpdated: boolean;
  /** Formatted time string e.g. "06:30 AM" */
  formattedTime: string;
  /** Formatted date string e.g. "Friday, 20 June 2026" */
  formattedDate: string;
  /** Tamil formatted date */
  formattedDateTa: string;
  /** Whether data is pending for today */
  isPendingToday: boolean;
}

/**
 * Format a Date object to IST time string like "06:30 AM"
 */
function formatTimeIST(date: Date): string {
  return date.toLocaleTimeString('en-IN', {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a Date object to IST date string like "Friday, 20 June 2026"
 */
function formatDateIST(date: Date, locale: 'en' | 'ta'): string {
  const localeStr = locale === 'ta' ? 'ta-IN' : 'en-IN';
  return date.toLocaleDateString(localeStr, {
    timeZone: IST_TIMEZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Get today's date string in IST (YYYY-MM-DD)
 */
function getTodayIST(): string {
  const now = new Date();
  const istDate = new Date(now.toLocaleString('en-US', { timeZone: IST_TIMEZONE }));
  const year = istDate.getFullYear();
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const day = String(istDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Fetch the latest market price update info from Supabase.
 * Falls back to current time if DB is unavailable.
 */
export async function fetchLatestMarketUpdateInfo(): Promise<MarketUpdateInfo> {
  const todayIST = getTodayIST();

  try {
    const { data, error } = await supabase
      .from('market_price_updates')
      .select('update_date, updated_at')
      .order('update_date', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      // Fallback: use current time
      const now = new Date();
      return {
        updatedAt: now.toISOString(),
        updateDate: todayIST,
        isTodayUpdated: false,
        formattedTime: formatTimeIST(now),
        formattedDate: formatDateIST(now, 'en'),
        formattedDateTa: formatDateIST(now, 'ta'),
        isPendingToday: true,
      };
    }

    const latestDate = data.update_date as string;
    const updatedAt = data.updated_at as string;
    const updatedAtDate = new Date(updatedAt);
    const isTodayUpdated = latestDate === todayIST;

    return {
      updatedAt,
      updateDate: latestDate,
      isTodayUpdated,
      formattedTime: formatTimeIST(updatedAtDate),
      formattedDate: formatDateIST(updatedAtDate, 'en'),
      formattedDateTa: formatDateIST(updatedAtDate, 'ta'),
      isPendingToday: !isTodayUpdated,
    };
  } catch {
    const now = new Date();
    return {
      updatedAt: now.toISOString(),
      updateDate: todayIST,
      isTodayUpdated: false,
      formattedTime: formatTimeIST(now),
      formattedDate: formatDateIST(now, 'en'),
      formattedDateTa: formatDateIST(now, 'ta'),
      isPendingToday: true,
    };
  }
}
